import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  Info,
  Calendar,
  XCircle,
  ArrowRight,
  Shield,
  AlertTriangle,
  FileText,
  Users,
  HardHat,
} from "lucide-react";

export default function HowToChooseRoofingContractorSaskatchewan() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id":
            "https://www.saskroofingcompany.com/blog/how-to-choose-roofing-contractor-saskatchewan#article",
          headline:
            "How to Choose a Roofing Contractor in Saskatchewan: 10 Questions to Ask",
          description:
            "Don't hire a roofer until you ask these 10 critical questions. Learn how to spot red flags, avoid storm chasers, and find the best roofing contractor in Saskatchewan.",
          author: { "@id": "https://www.saskroofingcompany.com/#organization" },
          publisher: {
            "@id": "https://www.saskroofingcompany.com/#organization",
          },
          datePublished: "2026-05-01",
          inLanguage: "en-CA",
          isPartOf: {
            "@id": "https://www.saskroofingcompany.com/blog#webpage",
          },
          mainEntityOfPage:
            "https://www.saskroofingcompany.com/blog/how-to-choose-roofing-contractor-saskatchewan",
          url: "https://www.saskroofingcompany.com/blog/how-to-choose-roofing-contractor-saskatchewan",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.saskroofingcompany.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.saskroofingcompany.com/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "How to Choose a Roofing Contractor",
              item: "https://www.saskroofingcompany.com/blog/how-to-choose-roofing-contractor-saskatchewan",
            },
          ],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How do I find a good roofing contractor in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Look for a local company with a physical Saskatchewan address, valid WCB and liability insurance, a history of 10+ years in the community, and a willingness to provide a detailed, fixed-price written quote rather than a verbal estimate.",
          },
        },
        {
          "@type": "Question",
          name: "What should I look for when hiring a roofer?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Verify their insurance coverage, ask for local references, ensure they use climate-appropriate materials (like impact-resistant shingles or proper ice and water shields), and insist on a comprehensive workmanship warranty.",
          },
        },
        {
          "@type": "Question",
          name: "Is it safe to hire a roofer that comes door-to-door after a storm?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Generally, no. Door-to-door roofers immediately following a hailstorm are often out-of-province 'storm chasers.' They may do rushed work and leave the province before warranty issues arise. Always vet contractors thoroughly.",
          },
        },
        {
          "@type": "Question",
          name: "How do I verify a roofing contractor's insurance and references?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Ask the contractor directly for their WCB clearance letter and a certificate of general liability insurance. For references, ask for addresses of homes they've roofed in your specific city within the last year.",
          },
        },
        {
          "@type": "Question",
          name: "Should I get multiple roofing quotes?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, getting 2 to 3 quotes is recommended. However, don't just look at the final price. Compare the scope of work, the materials specified, the warranty terms, and whether tear-off and disposal are explicitly included.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Choosing a Roofer in Saskatchewan | Sask Roofing"
        description="Don't hire a roofer until you ask these 10 critical questions. Learn how to spot red flags, avoid storm chasers, and find the best roofing contractor in SK."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/3039917b-a354-4173-9886-1842361cc0cd.png&output=webp&q=60"
            alt="Contractor discussing roofing quote with homeowner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 text-primary-foreground text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" /> Published May 1, 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              How to Choose a Roofing Contractor in Saskatchewan: 10 Questions
              to Ask
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Trust is everything in a market full of storm-chasers. Arm
              yourself with the right questions to ensure you hire a reliable,
              local professional who will stand behind their work.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Intro */}
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                Your roof is your home's first line of defense against
                Saskatchewan's brutal winters, high winds, and summer
                hailstorms. When it's time for a replacement or a major repair,
                hiring the right company is one of the most important decisions
                you'll make as a homeowner.
              </p>
              <p>
                Unfortunately, the roofing industry is notorious for "storm
                chasers"—out-of-province crews that flood into Saskatchewan
                after a major weather event, do rushed work with cheap
                materials, and disappear long before you realize your roof is
                leaking. To protect yourself and your investment, you need to
                know exactly how to vet the companies you invite onto your
                property.
              </p>
            </div>

            {/* TL;DR Box */}
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-xl shadow-sm">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground mb-3">
                <Shield className="w-6 h-6 text-primary" />
                The Golden Rule of Hiring a Roofer
              </h2>
              <p className="text-lg text-muted-foreground mb-0">
                Never hire a roofing contractor who cannot provide a physical
                Saskatchewan address, proof of WCB and liability insurance, and
                a detailed, fixed-price written quote. If they pressure you to
                sign "today only," show them the door.
              </p>
            </div>

            {/* The 10 Questions Section */}
            <div className="space-y-10">
              <h2 className="text-3xl font-bold text-foreground">
                The 10 Questions You Must Ask
              </h2>
              <p className="text-lg text-muted-foreground">
                Before you sign a contract or hand over a deposit, ask every
                prospective roofing contractor these ten questions. A reputable
                roofer will be happy to answer them; a shady one will dodge
                them.
              </p>

              {/* Q1 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  1
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  Are you local to Saskatchewan?
                </h3>
                <p className="text-muted-foreground">
                  Storm chasers leave after the storm. Out-of-town crews don't
                  honor warranties when they disappear back to their home
                  province. Local Saskatchewan roofers with a real, verifiable
                  address and a long history in the community are who you want.
                  If you have a warranty issue three years from now, you need to
                  know they will pick up the phone and drive to your house to
                  fix it.
                </p>
                <div className="mt-4">
                  <Link
                    to="/about"
                    className="text-primary font-medium hover:underline inline-flex items-center gap-1"
                  >
                    Read about Sask Roofing's local roots{" "}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Q2 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  2
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  Are you fully insured?
                </h3>
                <p className="text-muted-foreground">
                  This is non-negotiable. A contractor must carry Workers'
                  Compensation Board (WCB) coverage for their crew and general
                  liability insurance (minimum $2 million) to protect your
                  property. Ask for proof. If a worker is injured on your
                  property and the contractor isn't covered, you could be held
                  liable. If they can't show you a certificate of insurance,
                  walk away immediately.
                </p>
              </div>

              {/* Inline Photo 1 */}
              <figure className="my-10 rounded-xl overflow-hidden shadow-md">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/7ffb5273-5afc-4f80-87c1-9aa3abe2243d.png&output=webp&q=60"
                  alt="Reviewing a detailed roofing contract and checklist"
                  className="w-full h-auto object-cover aspect-[21/9]"
                />
                <figcaption className="bg-muted p-3 text-sm text-center text-muted-foreground">
                  Always insist on a detailed, written contract before any work
                  begins. Verbal agreements offer no protection.
                </figcaption>
              </figure>

              {/* Q3 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  3
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  Do you offer a fixed-price written quote?
                </h3>
                <p className="text-muted-foreground">
                  Never accept a verbal estimate or a "we'll figure it out as we
                  go" promise. Fixed pricing in writing protects you from
                  sudden, unexplained cost overruns. A professional quote should
                  itemize the materials, labor, tear-off, disposal, and taxes.
                  If you're wondering what a fair price looks like, check our{" "}
                  <Link
                    to="/roofing-cost-guide-saskatchewan"
                    className="text-primary hover:underline"
                  >
                    Saskatchewan roofing cost guide
                  </Link>
                  .
                </p>
              </div>

              {/* Q4 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  4
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  Will the crew be employees or subcontractors?
                </h3>
                <p className="text-muted-foreground">
                  Most roofing companies use subcontracted labor. That's
                  actually fine, provided the company tightly supervises the
                  crews, requires them to adhere to strict quality standards,
                  and holds them accountable. However, it matters who is on your
                  roof. Ask who the site supervisor will be and how the company
                  ensures the subcontractors are properly trained and insured.
                </p>
              </div>

              {/* Q5 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  5
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  What materials do you use?
                </h3>
                <p className="text-muted-foreground">
                  Brand, grade, and impact rating matter immensely. A contractor
                  who simply says "architectural shingles" without naming the
                  manufacturer (like IKO, CertainTeed, or GAF) hasn't thought
                  deeply about climate performance. In Saskatchewan, you want to
                  know if the shingles are rated for high winds and if they
                  offer Class 4 impact resistance against hail.
                </p>
              </div>

              {/* Q6 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  6
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  How do you handle ice damming?
                </h3>
                <p className="text-muted-foreground">
                  If a roofer in Saskatchewan can't clearly explain ice dam
                  prevention, they haven't worked here long enough. A proper
                  installation includes high-quality ice and water shield
                  membrane along the eaves and in the valleys, combined with an
                  assessment of your attic's ventilation and insulation.
                  Slapping on new shingles won't fix an underlying ice damming
                  issue.
                </p>
              </div>

              {/* Inline Photo 2 */}
              <figure className="my-10 rounded-xl overflow-hidden shadow-md">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/1e196a82-2c9f-4767-954d-4ed8c5a930aa.png&output=webp&q=60"
                  alt="Professional roofing crew working safely on a residential roof"
                  className="w-full h-auto object-cover aspect-[21/9]"
                />
                <figcaption className="bg-muted p-3 text-sm text-center text-muted-foreground">
                  A reputable contractor will prioritize safety, cleanliness,
                  and clear communication while their crew is on your property.
                </figcaption>
              </figure>

              {/* Q7 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  7
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  Is your quote inclusive of tear-off and disposal?
                </h3>
                <p className="text-muted-foreground">
                  Beware of quotes that look suspiciously cheap. "Materials
                  only" or "installation only" quotes on replacement jobs are
                  usually missing $1,500 to $3,000 in tear-off labor and
                  dumpster disposal fees. Ensure the contract explicitly states
                  that the removal and environmental disposal of your old roof
                  is included in the final price.
                </p>
              </div>

              {/* Q8 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  8
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  What's your warranty?
                </h3>
                <p className="text-muted-foreground">
                  There are two warranties on a new roof: the manufacturer's
                  warranty (which covers defective materials) and the
                  workmanship warranty (which covers installation errors). A
                  good contractor will offer a robust workmanship
                  warranty—usually 5 to 10 years. Get this in writing, and
                  again, ensure they are a local company that will actually be
                  around to honor it.
                </p>
              </div>

              {/* Q9 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  9
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  How do you handle change orders?
                </h3>
                <p className="text-muted-foreground">
                  Sometimes, tearing off the old roof reveals hidden problems,
                  like rotted plywood decking that must be replaced before new
                  shingles can go on. Ask the contractor upfront how they handle
                  these surprises. They should be able to provide a written
                  per-square-foot or per-sheet rate for replacing damaged
                  decking so you aren't held hostage by sudden price hikes.
                </p>
              </div>

              {/* Q10 */}
              <div className="bg-card border rounded-xl p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center font-bold text-xl rounded-bl-xl">
                  10
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4 pr-12">
                  Can I see your Saskatchewan-specific past work?
                </h3>
                <p className="text-muted-foreground">
                  Ask for references from real Saskatchewan customers. Request
                  photos of comparable projects—homes with a similar roof size,
                  similar pitch, and similar materials to yours. A proud
                  contractor will have a portfolio of local work readily
                  available and won't hesitate to provide references you can
                  actually call.
                </p>
              </div>
            </div>

            {/* Red Flags Table */}
            <div className="space-y-6 pt-8">
              <h2 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-destructive" />
                Contractor Red Flags
              </h2>
              <p className="text-lg text-muted-foreground">
                If you encounter any of these warning signs during the quoting
                process, we strongly recommend looking for another contractor.
              </p>

              <div className="overflow-hidden rounded-xl border shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="bg-destructive/10 p-6 md:border-r border-b md:border-b-0">
                    <h3 className="text-xl font-bold text-destructive mb-4 flex items-center gap-2">
                      <XCircle className="w-5 h-5" /> Red Flags (Run Away)
                    </h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>
                        • Door-to-door sales tactics immediately after a storm.
                      </li>
                      <li>
                        • "Today only" pricing or aggressive pressure to sign
                        immediately.
                      </li>
                      <li>
                        • Out-of-province license plates on their work trucks.
                      </li>
                      <li>
                        • Demanding a large cash deposit upfront before
                        materials arrive.
                      </li>
                      <li>
                        • Offering to "waive" or cover your insurance deductible
                        (this is insurance fraud).
                      </li>
                    </ul>
                  </div>
                  <div className="bg-green-500/10 p-6">
                    <h3 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" /> Green Flags (Hire
                      Them)
                    </h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>
                        • Invited to quote via your own research or a referral.
                      </li>
                      <li>
                        • Quotes are valid for 15-30 days, allowing you time to
                        decide.
                      </li>
                      <li>
                        • Established local presence with a verifiable local
                        address.
                      </li>
                      <li>
                        • Reasonable deposit structure tied to material
                        delivery.
                      </li>
                      <li>
                        • Professional, transparent communication and documented
                        processes.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="space-y-6 pt-8">
              <h2 className="text-3xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
              <Accordion
                type="single"
                collapsible
                className="w-full bg-card border rounded-xl px-6 py-2 shadow-sm"
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    How do I find a good roofing contractor in Saskatchewan?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Look for a local company with a physical Saskatchewan
                    address, valid WCB and liability insurance, a history of 10+
                    years in the community, and a willingness to provide a
                    detailed, fixed-price written quote rather than a verbal
                    estimate.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    What should I look for when hiring a roofer?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Verify their insurance coverage, ask for local references,
                    ensure they use climate-appropriate materials (like
                    impact-resistant shingles or proper ice and water shields),
                    and insist on a comprehensive workmanship warranty.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Is it safe to hire a roofer that comes door-to-door after a
                    storm?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Generally, no. Door-to-door roofers immediately following a
                    hailstorm are often out-of-province "storm chasers." They
                    may do rushed work and leave the province before warranty
                    issues arise. Always vet contractors thoroughly.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    How do I verify a roofing contractor's insurance and
                    references?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Ask the contractor directly for their WCB clearance letter
                    and a certificate of general liability insurance. For
                    references, ask for addresses of homes they've roofed in
                    your specific city within the last year.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-5">
                  <AccordionTrigger className="text-left font-bold text-lg">
                    Should I get multiple roofing quotes?
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-muted-foreground text-base leading-relaxed data-[state=closed]:h-0"
                    forceMount={true}
                  >
                    Yes, getting 2 to 3 quotes is recommended. However, don't
                    just look at the final price. Compare the scope of work, the
                    materials specified, the warranty terms, and whether
                    tear-off and disposal are explicitly included.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/95f4f5de-bf21-489b-a148-99109ad7ed60.png&output=webp&q=60')] opacity-10 bg-cover bg-center mix-blend-overlay"></div>
        <div className="container px-4 relative z-10 text-center max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Ready to Work with a Roofer You Can Trust?
          </h2>
          <p className="text-xl text-primary-foreground/90">
            We'll answer all 10 of these questions with complete transparency.
            No high pressure, just honest advice and a fixed written quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link
              to="/services/inspections"
              className="inline-flex items-center justify-center h-14 px-8 text-base font-bold bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
            >
              Book a Free Inspection
            </Link>
            <a
              href="tel:6397397969"
              className="inline-flex items-center justify-center h-14 px-8 text-base font-bold bg-transparent border-2 border-primary-foreground text-primary-foreground rounded-md hover:bg-primary-foreground/10 transition-colors"
            >
              Call (639) 739-7969
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
